/*
 * Created on 31.01.2020
 *
 * Copyright(c) 1995 - 2020 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { LoggingService } from '../utils/logging.service';

/**
 * Funktions, die einen Feldnamen aus einer Exception in den
 * Namen der FormControl übersetzt. Kann ein Array zurückgeben um mit
 * verschachtelten FormGroups zurechtzukommen.
 */
type MapFunction = (field: string) => string | string[];

/**
 * Lookup-Objekt, in dem der Feldname aus dem Bakcend auf den Namen
 * einer FormControl gemappt wird. Kann ein Array zurückgeben um mit
 * verschachtelten FormGroups zurechtzukommen.
 */
type MapObject = {[field: string]: string | string[]};

/**
 * Dieser Service stellt eine Funktion bereit um Fehler (z.B. Validierungsfehler),
 * die aus einem abgesendeten Formular resultieren an die entsprechenden
 * Formularfelder bzw. die gesamte FormGroup zu schreiben. Dann kann im UI
 * (mithilfe von i18n) eine richtige Fehlermeldung angezeigt werden.
 *
 * @author Philipp Schuster (philipp.schuster@t-systems.com)
 */
@Injectable({
    providedIn: 'root'
})
export class HttpFormErrorService {

    /**
     * Name des Fehlers an der FormGroup, wenn "globale" Exceptions fliegen. Das sind
     * Exceptions, die <strong>nur</strong> ein "message"-Feld haben, aber keine
     * genauere Spezifizierung. Wird genutzt für Exceptions, die nicht aus einer
     * Validierungs-Exception bzw. BindException resultieren. In dem Fall machen wir
     * auch kein i18n, weil die Fehlermeldung wie sie angezeigt werden soll direkt aus
     * der Backend-Exception kommt. Das ist dann auf die Sprache des Backends
     * beschränkt.
     *
     * Kann z.B. so angezeigt werden:
     * changePasswordForm.getError('GLOBAL_EXCEPTION_MSG')
     */
    public static readonly REGULAR_EXCEPTION_ERROR = 'GLOBAL_EXCEPTION_MSG';

    /**
     * Exceptions vom Backend, die diesen "Feldnamen" angeben sind dafür gedacht
     * "globale" i18n-Fehler über dem Formular anzuzeigen, d.h. keine Fehler,
     * die direkt an einem bestimmten Feld hängen. Es darf keine FormControl mit
     * dem Namen geben. Anders als bei {@link REGULAR_EXCEPTION_ERROR} ist
     * hier im Backend das "Feld" auf "global" gesetzt.
     */
    private static readonly RESERVED_GLOBAL_FIELD_NAME = 'global';

    constructor(private log: LoggingService) {
    }

    /**
     * Setzt nach einem fehlgeschlagenen Http-Request (400 - Bad Request)
     * (Absenden eines Formulars) die Fehlercodes an den entsprechenden FormControls bzw.
     * an der FormGroup. <strong>Andere 4XX-Fehler werden weitergeworfen!</strong>
     * (In zukünftiger Version kommt ggf. eine bessere Lösung. Derzeit müsste das hier
     * dann in ein try-catch gepackt werden.)
     *
     * Mithilfe der Fehlercodes kann an den Stellen an denen die FormGroup bzw. die
     * FormControls angezeigt werden eine i18n-Anzeige der Fehler erfolgen.
     * Das passiert in &lt;common-form-errors&gt; ({@link FormErrorsComponent}) bzw.
     * &lt;common-form-field&gt; ({@link FormFieldComponent}).
     *
     * <ul>
     *     <li>Verlässt sich darauf, dass {@link RESERVED_GLOBAL_FIELD_NAME} der
     *         reservierte "Feldname" für Fehler an der gesamten Formgroup ist
     *         (z.B. Fehler direkt über dem Formular).</li>
     *      <li>Behandelt nur 400 Bad Request-Fehler.</li>
     *      <li>Der Feldname in an den Server gesendetem Command muss gleich
     *          dem FormControl-Namen sein. Ansonsten muss der Mapper-Parameter
     *          genutzt werden.</li>
     * </ul>
     *
     * Wird vom Backend eine "normale" Exception geworfen, wird das "message"-
     * Field der Exception so wie es vom Backend kommt in die Error-Codes der
     * FormGroup reingeschrieben. Dafür wird der Key
     * {@link REGULAR_EXCEPTION_ERROR} genutzt. In der Regel will man das
     * dann direkt anzeigen und kein i18n für diesen Randfall machen.
     *
     * @param err HttpErrorResponse
     * @param form FormGroup
     * @param fieldToFormControlNameMapper Mapper um aus einem Feldfehler den Namen der FormControl bestimmen zu können.
     *                                     Unterstützt verschachtelte FormGroups mit String-Arrays, die den Pfad zur
     *                                     FormControl beschreiben.
     */
    assignErrorsToForm(err: HttpErrorResponse,
                       form: UntypedFormGroup,
                       fieldToFormControlNameMapper?: MapFunction | MapObject): void {
        // only 400 bad request errors
        if (!this.is400BadRequest(err)) {
            this.log.debug('HttpFormErrorService#assignErrorsToForm: 4XX (nicht 400) gefunden; weiterwerfen!');
            // wollen alles was wir hier nicht behandeln können weiter werfen
            throw err;
        }

        // haben eine Art von Exception erhalten; In den meisten Fällen eine selbst geworfene BindException
        // oder eine von @Valid abgelehnte Exception
        const ex_unspecified = err.error as Exception;

        // Falls wir eine "normale" Exception ohne errors-Field bekommen haben (keine Validierungsexception)
        // dann schreiben wir die tatsächliche Message direkt rein ins Formular; nutzen dafür einen speziellen Key
        if (ex_unspecified.message && !(<SpringValidationException> ex_unspecified).errors) {
            this.handleRegularException(form, ex_unspecified);
            return;
        }

        // versuchen Exception und Fehlercode zu parsen und den Fehlercode direkt ins Form zu schreiben
        this.log.debug('HttpFormErrorService#assignErrorsToForm: versuche Fehlercode aus Exception zu parsen');
        const fieldErrors = this.parseException(ex_unspecified as SpringValidationException);

        if (!fieldErrors || !fieldErrors.length) {
            // here be dragons
            // sollte nicht auftreten können, aber sicher ist sicher!
            this.log.debug(
                'HttpFormErrorService#assignErrorsToForm: konnte keine Fehlermeldung aus Exception extrahieren'
            );
            return;
        }

        this.debugInfo(fieldErrors, fieldToFormControlNameMapper);

        // Alle FeldError ins Formular schreiben
        fieldErrors.forEach(err => this.handleFieldError(form, err, fieldToFormControlNameMapper));
    }

    /**
     * Setzt den Fehler aus einer generellen Exception an ein Formular.
     *
     * @param form
     * @param ex
     */
    private handleRegularException(form: UntypedFormGroup, ex: Exception): void {
        this.log.debug(
            'HttpFormErrorService#handleRegularException: Keine ValidierungsException; normale Exception gefunden'
        );
        // haben eine "normale" Exception die 4XX produziert erhalten;
        // haben nur die Exception-Message und sonst keine Infos; Fehler am Form setzen
        const validationErrors: ValidationErrors = form.errors || {};
        validationErrors[HttpFormErrorService.REGULAR_EXCEPTION_ERROR] = ex.message;
        form.setErrors(validationErrors);
    }

    /**
     * Setzt einen Feldfehler an das Form bzw. die entsprechende Formgroup.
     */
    private handleFieldError(form: UntypedFormGroup, err: FieldError, fieldToFormControlNameMapper?: MapFunction | MapObject): void {
        // 'globale' Fehler sind i.d.R. die, die wir über dem Formular anzeigen statt an einem speziellen Feld.
        // können im UI z.B. so angezeigt werden:
        // <span *ngIf="changePasswordForm.hasError('noCaptcha')">{{i18n.changePasswordForm.errorMessages.noCaptcha}}</span>
        if (err.fieldName === HttpFormErrorService.RESERVED_GLOBAL_FIELD_NAME) {
            this.handleFormGroupError(form, err);
        } else {
            this.handleFormControlError(form, err, fieldToFormControlNameMapper);
        }
    }

    /**
     * Behandelt alle Feldfehler, deren Feldname gleich {@link RESERVED_GLOBAL_FIELD_NAME} ist und setzt
     * den Fehler an die FormGroup.
     */
    private handleFormGroupError(form: UntypedFormGroup, err: FieldError): void {
        this.log.debug(
            'HttpFormErrorService#handleFormGroupError: globale Fehlermeldung gefunden; Fehlercode: ' + err.errorCode
        );
        const validationErrors: ValidationErrors = form.errors || {};
        // das "true" ist hier egal; hauptsache der Fehlercode ist da, wodurch i18n funktioniert
        validationErrors[err.errorCode] = true;
        form.setErrors(validationErrors);
    }

    /**
     * Behandelt alle Feldfehler, die an FormControls gesetzt werden.
     */
    private handleFormControlError(form: UntypedFormGroup, err: FieldError, fieldToFormControlNameMapper: MapFunction | MapObject): void {
        this.log.debug(
            `HttpFormErrorService#handleFormControlError: Feldspezifische Fehlermeldung gefunden; ` +
            `Fehlercode='${err.errorCode}'; Feld='${err.fieldName}'`
        );

        const formControl = this.getFormControl(form, err.fieldName, fieldToFormControlNameMapper);

        if (formControl) {
            // Fehler, die wir direkt an einem FormField anzeigen wollen
            const validationErrors: ValidationErrors = {};
            // das "true" ist hier egal; hauptsache der Fehlercode ist als Key da,
            // wodurch i18n funktioniert
            validationErrors[err.errorCode] = true;
            formControl.setErrors(validationErrors);
        } else {
            this.log.error(
                `HttpFormErrorService#handleFormControlError: es gibt keine FormControl '${err.fieldName}';`
                + ` errorCode ist '${err.errorCode}'`
            );
        }
    }

    /**
     * Parst eine Response vom Typ {@link SpringValidationException} in
     * die eigentlichen Feld-Fehler.
     *
     * @param ex
     */
    private parseException(ex: SpringValidationException): FieldError[] {
        // Haben wir sehr wahrscheinlich eine eigene BindException geworfen in
        // der Art, wie der Userservice das macht:
        // BindException ex = new BindException(command, PASSWORD);
        //     ex.reject(INVALID_PASSWORD);
        //     throw ex

        if (!ex || !ex.errors) {
            // hier sollten wir zu dem Zeitpunkt eigentlich gar nicht mehr hinkommen :)
            this.log.error('HttpFormErrorService#parseException: Exception ist null oder hat kein "errors"-Property');
            return null;
        }

        const fes: FieldError[] = [];

        if ((<NotValidExceptionErrors> ex.errors[0]).field) {
            this.log.debug(`HttpFormErrorService#parseException: parsen als NotValidException`);
            // NotValidException
            const e = ex as NotValidException;
            e.errors.forEach(err => {
                // -2, weil an der Stelle dann sowas wie steht "Min.age"
                // => spezifiziert Fehler und das Feld, das fehlgeschlagen ist
                // => sinnvoller Key für i18n
                const validationConstraint = err.codes[err.codes.length - 2];
                fes.push(
                    new NotValidExceptionFieldError(err.field, validationConstraint)
                );
            });

        } else if ((<BindExceptionErrors> ex.errors[0]).objectName) {
            this.log.debug(`HttpFormErrorService#parseException: parsen als BindException`);
            // BindException
            const e = ex as BindException;
            e.errors.forEach(err => {
                fes.push(
                    new BindExceptionFieldError(err.objectName, err.code)
                );
            });
        } else {
            this.log.error(
                'HttpFormErrorService#parseException: Exception ist nicht null, aber auch keine ' +
                'BindException oder NotValidException');
            return null;
        }

        return fes;
    }

    /**
     * Überprüft, ob ein 400 Bad Request-Fehler vorliegt. Nur solche behandeln wir hier.
     * Andere 4XX-Fehler werden hier nicht behandelt.
     * 5XX Fehler werden von {@link HttpErrorInterceptor} gehandhabt.
     * @param err
     */
    private is400BadRequest(err: HttpErrorResponse): boolean {
        if (err.status !== 400) {
            this.log.debug(
                'HttpFormErrorService#is400BadRequest: Es werden nur 400 Bad Request-Fehler unterstützt'
            );
            return false;
        } else {
            return true;
        }
    }

    /**
     * Gibt die FormControl zurück, die zu einem FeldFehler gehört.
     * Hier wird, sofern angegeben, der "fieldToFormControlNameMapper" genutzt.
     */
    private getFormControl(form: UntypedFormGroup,
                           fieldName: string,
                           fieldToFormControlNameMapper?: MapFunction | MapObject): UntypedFormControl {
        let path: string | string[];
        if (typeof fieldToFormControlNameMapper === 'function') {
            path = fieldToFormControlNameMapper(fieldName);
        } else if (typeof fieldToFormControlNameMapper === 'object') {
            path = fieldToFormControlNameMapper[fieldName];
        } else {
            path = fieldName;
        }

        let pathDebugStr = fieldName;
        if (typeof path !== 'string') {
            // ist array und kein string
            pathDebugStr = path.reduce((a, b) => `${a} => ${b}`);
        }
        this.log.debug(`HttpFormErrorService#getFormControl#getFormControl: fieldName='${fieldName}', formControlPfad='${pathDebugStr}'`);

        // get kann mit String-Array umgehen
        return form.get(path) as UntypedFormControl;
    }

    /**
     * Gibt Debug-Infos aus.
     *
     * @param fieldErrors
     * @param fieldToFormControlNameMapper
     */
    private debugInfo(fieldErrors: FieldError[], fieldToFormControlNameMapper: MapFunction | MapObject) {
        if (fieldErrors[0] instanceof NotValidExceptionFieldError) {
            this.log.debug(
                'HttpFormErrorService#debugInfo: Antwort vom Backend ist fehlgeschlagene @Valid-Validierung'
            );
        } else {
            this.log.debug(
                'HttpFormErrorService#debugInfo: Antwort vom Backend ist manuell geworfene BindException'
            );
        }

        this.log.debug(
            'HttpFormErrorService#debugInfo: alle Fehlercodes:'
        );
        fieldErrors.forEach(err => this.log.debug(`Feld=${err.fieldName}, Code=${err.errorCode}`));

        if (typeof fieldToFormControlNameMapper === 'function') {
            this.log.debug('HttpFormErrorService#debugInfo: Lookup-Function für FormControlName benutzen');
        } else if (typeof fieldToFormControlNameMapper === 'object') {
            this.log.debug('HttpFormErrorService#debugInfo: Lookup-Objekt für FormControlName benutzen');
        }/* else if (!fieldToFormControlNameMapper) {

        }*/
    }
}

/**
 * Oberklasse für alle Exceptions.
 */
export interface Exception {
    /** Message der Java-Exception. */
    message: string;
}

/**
 * Gemeinsame Properties von {@link NotValidException} und {@link BindException}.
 */
export interface SpringValidationException extends Exception {
    /** Zeitstempel. */
    timestamp: string;
    /** HTTP-Fehlercode. */
    status: number;
    /** Name des HTTP-Fehlercodes. */
    error: string;
    /** Auf Server aufgerufener Pfad. */
    path: string;
    /**
     * Detaillierte Auflistung, welches Feld oder welche Felder nicht erfolgreich
     * validiert werden konnten.
     */
    errors: SpringValidationExceptionErrors[];
}

/**
 * Gemeinsame Properties von {@link NotValidException#errors} und {@link BindException#errors}.
 */
interface SpringValidationExceptionErrors {
    /**
     * Eine Repräsentation des fehlgeschlagenen Validierung der Form (Beispiel):
     * <pre>
     * "codes": [
     *     "Min.myCommand.age",
     *     "Min.int",
     *     "Min.age",
     *     "Min"
     * ]
     * </pre>
     */
    codes: string[];
    /**
     * Name des Objektes, das betroffen ist. Im Falle von "@Valid" wird das der Name
     * der Variable sein, z.B. vom Command. Im Falle von selbst geworfenen BindExceptions
     * wird das Feld für den Feldnamen missbraucht.
     */
    objectName: string;
}

/**
 * Klasse, die eine von Spring durch eine fehlerhafte "@Valid"-Validierung geworfene
 * Fehlerantwort repräsentiert.
 */
export interface NotValidException extends SpringValidationException {
    errors: NotValidExceptionErrors[];
}

/**
 * Klasse, die eine selbst geworfene BindException wirft. Das ist sinnvoll um
 * Feldgenaue Validierung zu ermöglichen.
 */
export interface BindException extends SpringValidationException {
    /**
     * Detaillierte Auflistung, welches Feld oder welche Felder nicht erfolgreich
     * validiert werden konnten.
     */
    errors: BindExceptionErrors[];
}

/**
 * Siehe {@link NotValidException#errors}.
 */
interface NotValidExceptionErrors extends SpringValidationExceptionErrors {
    /** Ähnlich wie {@link SpringValidationExceptionErrors#codes}.*/
    arguments: { codes: string[]; defaultMessage: string; code: string }[];
    /**
     * Von Spring generierte Fehlermeldung, die den Fehler in Textform beschreibt.
     * Zum Beispiel "muss größer oder gleich 18 sein".
     */
    defaultMessage: string;
    /**
     * Das Feld (Property) der Klasse, bei dem der Validierungsfehler aufgetreten ist.
     */
    field: string;
    /**
     * Der ungültige Wert.
     */
    rejectedValue: any;
    /**
     * Ob dieser Fehler einen Binding-Fehler repräsentiert (wie ein Typ-Fehler; true)
     * oder ob es ein Validierungsfehler ist (false).
     */
    bindingFailure: boolean;
}

/**
 * Siehe {@link BindExceptionErrors#errors}.
 */
interface BindExceptionErrors extends SpringValidationExceptionErrors {
    /** Ähnlich wie {@link SpringValidationExceptionErrors#codes}.*/
    codes: string[];
    /** Der im Backend spezifizierte Code, der den Key für ein i18n-Mapping bereitstellt. */
    code: string;
}

/**
 * Das wesentliche Ergebnis einer {@link NotValidException} oder {@link BindException}
 */
abstract class FieldError {
    /** Der Name des Feldes, das nicht erfolgreich validiert. */
    private readonly _fieldName: string;

    /** Fehlercode. In der Regel ein Code, der der Key für ein i18n-Mapping ist. */
    private readonly _errorCode: string;

    protected constructor(fieldName: string, errorCode: string) {
        this._fieldName = fieldName;
        this._errorCode = errorCode;
    }

    get fieldName(): string {
        return this._fieldName;
    }

    get errorCode(): string {
        return this._errorCode;
    }
}

/**
 * Die wesentlichen Informationen einer {@link NotValidException}.
 */
export class NotValidExceptionFieldError extends FieldError {

    constructor(fieldName: string, errorCode: string) {
        super(fieldName, errorCode);
    }
}

/**
 * Die wesentlichen Informationen einer {@link BindException}.
 */
export class BindExceptionFieldError extends FieldError {

    constructor(fieldName: string, errorCode: string) {
        super(fieldName, errorCode);
    }

}
